import React from 'react';
import { Document, Page, Text, View, Image, PDFDownloadLink } from '@react-pdf/renderer';
import { professionalPDFStyles, pdfUtils, PDFHeader, PDFFooter, PDFPageNumber, CandidateSignatureBox, ReferenceSignatureBox, PassportPhoto } from './PDFUtils';
import logoLight from '../../assets/images/logo-light.png';

// PDF Document Component with simple signature boxes
const ApplicationPDFDocument = ({ schema, formData, applicantName, passportPhotoData }) => (
  <Document>
    <Page size="A4" style={professionalPDFStyles.page}>
      {/* Header */}
      <PDFHeader 
        title="Moeen Memorial Foundation"
        subtitle="Educational Aid Application Form"
        logoSrc={logoLight}
      />

      {/* Application Metadata with Photo */}
      <View style={professionalPDFStyles.applicationMetaWithPhoto}>
        <View style={professionalPDFStyles.applicantInfo}>
          <View style={professionalPDFStyles.metaRow}>
            <Text style={professionalPDFStyles.metaLabel}>Application #:</Text>
            <Text style={professionalPDFStyles.metaValue}>{formData.applicationNumber || formData.id || 'N/A'}</Text>
          </View>
          <View style={professionalPDFStyles.metaRow}>
            <Text style={professionalPDFStyles.metaLabel}>Applicant Name:</Text>
            <Text style={professionalPDFStyles.metaValue}>{applicantName || formData.mMApplicantName || 'N/A'}</Text>
          </View>
          <View style={professionalPDFStyles.metaRow}>
            <Text style={professionalPDFStyles.metaLabel}>Email:</Text>
            <Text style={professionalPDFStyles.metaValue}>{formData.email || 'N/A'}</Text>
          </View>
        </View>
        
        {/* Passport Photo Section - Always show photo area */}
        <View style={professionalPDFStyles.photoSection}>
          <PassportPhoto 
            photoId={formData.passportPhotoId}
            apiUrl={process.env.REACT_APP_ESPO_API_URL}
            photoData={passportPhotoData}
          />
          <Text style={professionalPDFStyles.photoLabel}>Passport Photo</Text>
        </View>
      </View>

      {/* Application Content */}
      {schema
        .filter(section => !section.isInstructionPage) // Exclude instruction pages from PDF
        .map((section) => (
        <View key={section.id} style={{ marginBottom: 15 }} wrap={false}>
          <Text style={professionalPDFStyles.sectionTitle}>
            Step {section.step}: {section.title}
          </Text>
          
          {section.description && (
            <Text style={professionalPDFStyles.sectionDescription}>
              {section.description}
            </Text>
          )}

          {/* Handle sections with subsections */}
          {section.haveSections && section.sections ? (
            section.sections.map((subsection) => (
              <View key={subsection.id} style={{ marginBottom: 12 }}>
                <Text style={professionalPDFStyles.subsectionTitle}>{subsection.title}</Text>
                
                {/* For community reference sections, add signature box on the right */}
                {section.title === "Community References" ? (
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <View style={{ width: '65%' }}>
                      {subsection.fields.map((field) => (
                        <View key={field.id} style={professionalPDFStyles.fieldRow}>
                          <Text style={professionalPDFStyles.fieldLabel}>{field.label}:</Text>
                          <Text style={pdfUtils.getFieldValueStyle(formData[field.name], field, professionalPDFStyles)}>
                            {pdfUtils.formatValue(formData[field.name], field, formData)}
                          </Text>
                        </View>
                      ))}
                    </View>
                    <ReferenceSignatureBox />
                  </View>
                ) : (
                  /* Regular subsection without signature box */
                  subsection.fields.map((field) => (
                    <View key={field.id} style={professionalPDFStyles.fieldRow}>
                      <Text style={professionalPDFStyles.fieldLabel}>{field.label}:</Text>
                      <Text style={pdfUtils.getFieldValueStyle(formData[field.name], field, professionalPDFStyles)}>
                        {pdfUtils.formatValue(formData[field.name], field, formData)}
                      </Text>
                    </View>
                  ))
                )}
              </View>
            ))
          ) : (
            /* Handle sections with direct fields */
            section.fields?.map((field) => (
              <View key={field.id} style={professionalPDFStyles.fieldRow}>
                <Text style={professionalPDFStyles.fieldLabel}>{field.label}:</Text>
                <Text style={pdfUtils.getFieldValueStyle(formData[field.name], field, professionalPDFStyles)}>
                  {pdfUtils.formatValue(formData[field.name], field, formData)}
                </Text>
              </View>
            ))
          )}
        </View>
      ))}

      {/* Small candidate signature box at bottom right */}
      <CandidateSignatureBox applicantName={applicantName || formData.mMApplicantName} />

      {/* Footer */}
      <PDFFooter 
        organizationName="Moeen Memorial Foundation Application System"
        additionalText="For any queries, please contact the foundation office"
      />

      {/* Page Number */}
      <PDFPageNumber />
    </Page>
  </Document>
);

// Main PDF Generator Component
const ApplicationPDFGenerator = ({ 
  schema, 
  formData, 
  applicantName, 
  fileName,
  buttonSize = 'md',
  buttonStyle = {},
  buttonIcon = '',
  buttonText = '📥 Download PDF',
  buttonTitle = 'Download PDF'
}) => {
  const [passportPhotoData, setPassportPhotoData] = React.useState(null);
  const [isLoadingPhoto, setIsLoadingPhoto] = React.useState(false);

  // Fetch passport photo if it exists
  React.useEffect(() => {
    const fetchPhoto = async () => {
      if (formData.passportPhotoId) {
        setIsLoadingPhoto(true);
        try {
          const photoBase64 = await pdfUtils.fetchImageAsBase64(
            formData.passportPhotoId,
            process.env.REACT_APP_ESPO_API_URL,
            process.env.REACT_APP_ESPO_API_KEY
          );
          setPassportPhotoData(photoBase64);
        } catch (error) {
          console.error('Failed to fetch passport photo:', error);
          setPassportPhotoData(null);
        } finally {
          setIsLoadingPhoto(false);
        }
      }
    };

    fetchPhoto();
  }, [formData.passportPhotoId]);

  // Validate data before generating PDF
  const validation = pdfUtils.validateFormData(formData, schema);
  
  if (!validation.isValid) {
    return (
      <div style={{
        padding: '10px 20px',
        backgroundColor: '#F8D7DA',
        color: '#721C24',
        borderRadius: '5px',
        fontSize: '14px',
        border: '1px solid #F5C6CB'
      }}>
        Cannot generate PDF: {validation.errors.join(', ')}
      </div>
    );
  }

  const defaultFileName = pdfUtils.generateFileName(
    'Moeen_Memorial_Application',
    applicantName || formData.mMApplicantName,
    formData.id
  );

  const defaultButtonStyle = {
    textDecoration: 'none',
    padding: buttonSize === 'sm' ? '6px 12px' : '10px 20px',
    color: '#fff',
    backgroundColor: '#2E86C1',
    borderRadius: '5px',
    display: 'inline-block',
    fontSize: buttonSize === 'sm' ? '12px' : '14px',
    fontWeight: 'bold',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    ...buttonStyle
  };
  
  return (
    <PDFDownloadLink
      document={
        <ApplicationPDFDocument 
          schema={schema} 
          formData={formData} 
          applicantName={applicantName}
          passportPhotoData={passportPhotoData}
        />
      }
      fileName={fileName || defaultFileName}
      style={defaultButtonStyle}
      title={buttonTitle}
    >
      {({ blob, url, loading, error }) => {
        if (loading || isLoadingPhoto) {
          return buttonIcon ? <i className={buttonIcon}></i> : '📄 Generating...';
        }
        if (error) return buttonIcon ? <i className="fas fa-exclamation-triangle"></i> : '❌ Error';
        return (
          <>
            {buttonIcon && <i className={buttonIcon}></i>}
            {buttonText && <span className={buttonIcon ? 'ms-1' : ''}>{buttonText}</span>}
          </>
        );
      }}
    </PDFDownloadLink>
  );
};

// Export both the generator and the document component for reuse
export { ApplicationPDFDocument };
export default ApplicationPDFGenerator;
